import { Button, Grid, ThemeProvider } from '@mui/material'
import { TERRORCODE, TGLOBAL, TNamespace, createThemeWithMode, isElectron, uuidv4 } from '@sicarx/sxw-core'
import { Fragment, Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { SnackbarKey, useSnackbar } from 'notistack';
import { DrawerM, NotificationDialog, ServerPrint, SicarLoading, TooltipSX, useAlert } from '@sicarx/sxw-components';
import { IndexRoutes } from './routes'
import { PrinterSettingsEntity, RootLocalState, useAuthAccountStore, useAuthCompanyStore, useDrawerStore, useHandlerUpgradeNotificationStore, useLocalStore, useOnlineStore } from '@sicarx/sxw-api';
import { UpdatePaymentCard } from './components/UpdatePaymentCard';
import { useTranslation } from "react-i18next";
import shallow from 'zustand/shallow';
import { device_identifier_db } from "@sicarx/sxw-api"

import useSubscriptionSync from './hooks/useSusbscriptionSync'

const Feedback = lazy(() => import("@sicarx/sxw-components").then(({ Feedback }) => ({ default: Feedback })));
const TimbresDialog = lazy(() => import("@sicarx/sxw-components").then(({ TimbresDialog }) => ({ default: TimbresDialog })));
const UpdatePermissionsAlert = lazy(() => import("./components/UpdatePermissionsAlert").then(({ UpdatePermissionsAlert }) => ({ default: UpdatePermissionsAlert })));
const SubscriptionDialog = lazy(() => import("@sicarx/sxw-components").then(({ SubscriptionDialog }) => ({ default: SubscriptionDialog })));

function App() {
    let snackBarOffline: SnackbarKey;

    const [isMounted, setIsMounted] = useState<boolean>(false);

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { drawerOpen, drawer } = useDrawerStore();
    const isDarkMode = useLocalStore(state => state.darkMode.isDarksMode);
    const isDevelopment = import.meta.env.VITE_TESTING === 'PROD';
    const mode = isDarkMode ? 'dark' : 'light';
    const { setShowing, handlerUpgradeNotification: { notifications, showing }, /* resetHandlerUpgradeNotification, setNotification, */ cleanShowed, setShowed } = useHandlerUpgradeNotificationStore();
    const { t } = useTranslation();
    const { /* Alert, dialogProps, */ openDialog/* , closeDialog */ } = useAlert();
    const { setOnline } = useOnlineStore()

    //Development
    const theme = useMemo(() => createThemeWithMode(mode, localStorage?.getItem("mainColor") ? false : isDevelopment), [mode, localStorage?.getItem("mainColor") ? false : isDevelopment]);

    const accountStatus = useAuthAccountStore(store => store.authAccount.status);
    const isCompanyLogged = useAuthCompanyStore(store => store.authCompany.logged);
    const jwt = useAuthCompanyStore(store => store.authCompany.jwt);
    const hideVerifyDialog = useAuthCompanyStore(store => store.authCompany.hideVerifyDialog);
    const isInitConfigComplete = useAuthCompanyStore(store => store.authCompany.isInitConfigComplete);
    const isLogged = useAuthCompanyStore(store => store.authCompany.logged);
    const checkStatus = useAuthAccountStore(store => store.checkStatus);
    const getPrintersSettings: PrinterSettingsEntity[] = useLocalStore((store: RootLocalState) => store?.printerSettings, shallow);
    /* const hideVerifyAccountDialog = useAuthCompanyStore(store => store.hideVerifyAccountDialog); */

    useSubscriptionSync()
    //Production
    //const theme = useMemo(() => createThemeWithMode(mode), [mode]);
    useEffect(() => {
        if (notifications.length && showing === false) {
            setShowing(true).then(() => {

                notifications.filter((x) => x.showed === false).forEach(async (_, index) => {
                    /* enqueueSnackbar(`${x.url}-${x.code}`, {
                        variant: 'info',
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'right'
                        }
                    }); */
                    await NotificationDialog({
                        type: "info",
                        description: "Servicio en mantenimiento, intenta de nuevo más tarde"
                    })
                    setShowed(notifications[index].id)
                })
            }).finally(() => {
                setTimeout(() => {
                    cleanShowed()
                    setShowing(false)
                }, 3000);
            })
        }
    }, [notifications, showing])
    useEffect(() => {
        if (notifications.length && showing === false) {
            setShowing(true).then(() => {
                notifications.filter((x) => x.showed === false).forEach(async (_, index) => {
                    await NotificationDialog({
                        type: "info",
                        description: `${t(TERRORCODE.GENERIC_423, { ns: "ERRORCODE" })}`
                    })
                    setShowed(notifications[index].id)
                })
            }).finally(() => {
                setTimeout(() => {
                    cleanShowed()
                    setShowing(false)
                }, 3000);
            })
        }
    }, [notifications, showing])

    useEffect(() => {
        setIsMounted(true);

        //console.log("location", location)
        window.addEventListener("online", () => {

            setOnline(true)

            enqueueSnackbar('Conectado', {
                variant: 'success',
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right'
                }
            });

            closeSnackbar(snackBarOffline)
        });

        window.addEventListener("offline", () => {

            setOnline(false)

            snackBarOffline = enqueueSnackbar('Desconectado', {
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right'
                },
            });

        });
        window.addEventListener('beforeunload', function () {
            const dfMessengerBubble: any = document.querySelector('df-messenger-chat-bubble');
            if (dfMessengerBubble) {
                dfMessengerBubble?.closeChat();
            }
        });
        return () => {
            setIsMounted(false);
            window.removeEventListener("beforeunload", () => {
                setOnline(false)
                console.log("Estamos fuera de linea")
            });
            window.removeEventListener("offline", () => {
                setOnline(false)
                console.log("Estamos fuera de linea")
            });
            window.removeEventListener("online", () => {
                setOnline(true)
                console.log("Estamos en linea")

            });
        }
    }, []);

    useEffect(() => {
        if (!hideVerifyDialog && isMounted && isCompanyLogged && isInitConfigComplete && accountStatus === 0)
            setTimeout(() => {
                openDialog({
                    title: `${t(TGLOBAL.WARNING, { ns: 'global' })}`,
                    message: 'Recuerda verificar tu correo electrónico',
                });
            }, 500);
    }, [isMounted, hideVerifyDialog, isCompanyLogged, isInitConfigComplete, accountStatus]);

    useEffect(() => {
        if (isMounted) {
            (async () => {
                const dfMessenger: any = document.querySelector('df-messenger');
                if (dfMessenger && jwt !== "") {


                    const payload = [
                        {
                            "type": "info",
                            "title": "SICARX",
                            "subtitle": "Hola, estoy aquí para ayudarte con cualquier pregunta que tengas sobre SICAR X Punto de Venta.",
                            "image": {
                                "rawUrl": "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwNyIgdmlld0JveD0iMCAwIDEwMCAxMDciIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik01MCAyMS41NzgzTDc4LjU3MTQgNTAuMjlIMTAwTDUwIDBMMCA1MC4xMTE3SDIxLjQyODZMNTAgMjEuNTc4M1pNNTAgODUuNkwyMS40Mjg2IDU2Ljg4ODNIMEw1MCAxMDdMNzUgODEuODU1TDEwMCA1Ni43MUg3OC41NzE0TDUwIDg1LjZaIiBmaWxsPSIjMDg1REE5Ii8+Cjwvc3ZnPgo="
                            },
                            "anchor": {
                                "href": "https://www.sicarx.com",
                                "target": "_blank"
                            }
                        }

                    ];
                    const queryParameters = {
                        parameters: {
                            token: jwt
                        }
                    };
                    dfMessenger.setQueryParameters(queryParameters);
                    if (sessionStorage.getItem("chatOpened") !== "0") {
                        dfMessenger?.renderCustomCard(payload)!;
                    }
                    sessionStorage.setItem("chatOpened", "0")
                }
                const deviceId = await device_identifier_db.device.toCollection().first()
                if (!deviceId) {
                    await device_identifier_db.device.add({
                        deviceIdentifier: uuidv4()
                    })
                }
            })()
            if (accountStatus !== 1)
                (async () => await checkStatus())();
        }
    }, [isMounted, jwt]);
    useEffect(() => {
        if (drawer.open) {
            const dfMessengerBubble: any = document.querySelector('df-messenger-chat-bubble');
            if (dfMessengerBubble) {
                dfMessengerBubble?.closeChat();
            }
        }
    }, [drawer.open])

    return (<>
        <ThemeProvider theme={theme}>
            {location.pathname === "/updatePaymentCard" ?
                <UpdatePaymentCard />
                :
                location.pathname === "/buyChips" ?
                    <>
                        <SicarLoading />
                        <TimbresDialog fromMobile />
                    </>
                    :
                    <>
                        {
                            location.pathname === "/didCompleteSubscription" ?
                                <Suspense>
                                    <SicarLoading />
                                    <IndexRoutes />
                                </Suspense>
                                :
                                <>
                                    {getPrintersSettings?.length > 0 && isElectron() && <ServerPrint />}
                                    <DrawerM
                                        children={
                                            <TooltipSX
                                                title={
                                                    <Fragment>
                                                        <Grid container direction="column" sx={{ display: "flex", alignContent: "center", justifyContent: "center" }}>
                                                            <Grid item sx={{ justifyContent: "center", display: "flex", flexDirection: "column" }}>
                                                                {t(TGLOBAL.HELP, { ns: TNamespace.GLOBAL })}
                                                            </Grid>

                                                        </Grid>
                                                    </Fragment>
                                                }
                                            >



                                                <Button sx={{ minWidth: "44px", height: "44px", padding: "0" }} onClick={() => {
                                                    const dfMessengerBubble: any = document.querySelector('df-messenger-chat-bubble');
                                                    if (dfMessengerBubble) {
                                                        dfMessengerBubble?.openChat();
                                                        setTimeout(() => {
                                                            const textarea = document
                                                                .querySelector('df-messenger')
                                                                ?.querySelector('df-messenger-chat-bubble')
                                                                ?.shadowRoot
                                                                ?.querySelector('df-messenger-chat')
                                                                ?.shadowRoot
                                                                ?.querySelector('df-messenger-user-input')
                                                                ?.shadowRoot
                                                                ?.querySelector('.input-box') as HTMLTextAreaElement;
                                                            if (textarea) {
                                                                textarea.focus();
                                                                textarea.select();
                                                            }
                                                        }, 100);

                                                        drawerOpen(false);
                                                    }
                                                }}>
                                                    <img style={{ width: "34px", borderRadius: "100%", padding: "5px", background: "#085DA9" }} src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE0IDJDMTUuNjU2OSAyIDE3IDMuMzQzMTUgMTcgNVY4SDE5QzIwLjY1NjkgOCAyMiA5LjM0MzE1IDIyIDExVjE3LjM3NUMyMiAxOC44MjQ3IDIwLjgyNDcgMjAgMTkuMzc1IDIwVjIwLjc3MzRMMTkuMzYzMyAyMC45Mjk3QzE5LjM2MzMgMjIuMjcxOSAxOSAyMi41IDE3LjgzNTkgMjEuNjE2MkwxNy43MTA5IDIxLjUyMDVMMTYgMjBIMTBDOC4zNDMxNSAyMCA3IDE4LjY1NjkgNyAxN1YxNC44ODg3TDYuMjg5MDYgMTUuNTIwNUw2LjE2NDA2IDE1LjYxNjJDNS41NjM1IDE2LjAwNjkgNC43MzE1MiAxNi43MDg3IDQuNjI1IDE2VjE0QzMuMTc1MjUgMTQgMiAxMi44MjQ3IDIgMTEuMzc1VjVDMiAzLjM0MzE1IDMuMzQzMTUgMiA1IDJIMTRaTTExIDEzQzEwLjQ0NzcgMTMgMTAgMTMuNDQ3NyAxMCAxNEMxMCAxNC41NTIzIDEwLjQ0NzcgMTUgMTEgMTVDMTEuNTUyMyAxNSAxMiAxNC41NTIzIDEyIDE0QzEyIDEzLjQ0NzcgMTEuNTUyMyAxMyAxMSAxM1pNMTQuNSAxM0MxMy45NDc3IDEzIDEzLjUgMTMuNDQ3NyAxMy41IDE0QzEzLjUgMTQuNTUyMyAxMy45NDc3IDE1IDE0LjUgMTVDMTUuMDUyMyAxNSAxNS41IDE0LjU1MjMgMTUuNSAxNEMxNS41IDEzLjQ0NzcgMTUuMDUyMyAxMyAxNC41IDEzWk0xOCAxM0MxNy40NDc3IDEzIDE3IDEzLjQ0NzcgMTcgMTRDMTcgMTQuNTUyMyAxNy40NDc3IDE1IDE4IDE1QzE4LjU1MjMgMTUgMTkgMTQuNTUyMyAxOSAxNEMxOSAxMy40NDc3IDE4LjU1MjMgMTMgMTggMTNaTTUgMy41QzQuMTcxNTcgMy41IDMuNSA0LjE3MTU3IDMuNSA1VjExLjM3NUMzLjUgMTEuOTk2MyA0LjAwMzY4IDEyLjUgNC42MjUgMTIuNUg2LjEyNVYxMy42NTkyTDcgMTIuODgxOFYxMUM3IDkuMzQzMTUgOC4zNDMxNSA4IDEwIDhIMTUuNVY1QzE1LjUgNC4xNzE1NyAxNC44Mjg0IDMuNSAxNCAzLjVINVoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=' />
                                                </Button>
                                            </TooltipSX>
                                        }
                                    >
                                    </DrawerM>

                                    <Suspense>
                                        <TimbresDialog />
                                    </Suspense>
                                    <Suspense>
                                        {isLogged && (
                                            <SubscriptionDialog />
                                        )}
                                    </Suspense>
                                    <SicarLoading />
                                    <Suspense>
                                        <Feedback />
                                    </Suspense>
                                    <Suspense>
                                        <IndexRoutes />
                                    </Suspense>
                                    <Suspense>
                                        <UpdatePermissionsAlert />
                                    </Suspense>
                                </>
                        }

                    </>
            }
        </ThemeProvider>
    </>)
}

export default App;